/**
 * MyVolunteersView — candidate self-service volunteer provisioning (outreach MVP).
 *
 * A CANDIDATE user adds their own field volunteers, each bounded to a subset of
 * the candidate's authoritative turf. The scope a volunteer can be granted can
 * never exceed the candidate's reach — the backend enforces this (403), and
 * this UI only ever OFFERS in-reach options so the common path never provokes
 * one.
 *
 * Flow:
 *   - Role gate (CANDIDATE only) as defense-in-depth; server enforces too.
 *   - GET /candidates/mine on mount; the delegable candidate is the first row
 *     with relation === 'candidate' && can_delegate. Typical tenant has exactly
 *     one.
 *   - A create form: name / email / temp password / role + a scope picker built
 *     from the candidate's own scopes (subregion → precinct chips, ld → single
 *     toggle). POST /candidates/{id}/staff on submit.
 *
 * There is no staff LIST — no GET-staff endpoint exists yet. A create form is
 * the whole surface; provisioned volunteers appear in the admin user list.
 *
 * Pattern mirrors LeadsView / CandidatesAdminView: axios + useState/useEffect,
 * semantic Tailwind tokens, ViewHeader, useIsMobile for structural mobile swaps.
 * Strictly typed (no @ts-nocheck).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  UserPlus,
  ShieldAlert,
  MapPin,
  Users,
  MailWarning,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { ViewHeader } from '@/components/ViewHeader';
import { Skeleton } from '@/components/Skeleton';
import { useIsMobile } from '@/hooks/useMediaQuery';
import {
  listMyCandidates,
  listCandidateStaff,
  delegateStaff,
  type MyCandidate,
  type CandidateReachScope,
  type CandidateStaffMember,
  type DelegatedRole,
  type DelegateScopeInput,
} from '@/api/delegation';

// ---------------------------------------------------------------------------
// Scope selection model
//
// One entry per reach row on the candidate, keyed by its index in `scopes`.
// - subregion → a Set of selected precinct strings (default: all).
// - ld        → a boolean "entire LD" toggle (default: on).
// The submitted `scopes` are rebuilt from this at POST time; a group with no
// selection is omitted entirely.
// ---------------------------------------------------------------------------

interface SubregionSelection {
  kind: 'subregion';
  ld: string;
  precincts: string[]; // all offerable precincts for this group
  selected: Set<string>;
}
interface LdSelection {
  kind: 'ld';
  ld: string;
  on: boolean;
}
type ScopeSelection = SubregionSelection | LdSelection;

function initSelections(scopes: CandidateReachScope[]): ScopeSelection[] {
  return scopes.map((s): ScopeSelection => {
    if (s.kind === 'subregion') {
      const precincts = s.precincts ?? [];
      return {
        kind: 'subregion',
        ld: s.ld,
        precincts,
        selected: new Set(precincts), // default all on
      };
    }
    return { kind: 'ld', ld: s.ld, on: true };
  });
}

/** Build the POST payload's `scopes` from the current selections. */
function buildScopes(selections: ScopeSelection[]): DelegateScopeInput[] {
  const out: DelegateScopeInput[] = [];
  for (const sel of selections) {
    if (sel.kind === 'subregion') {
      const picked = sel.precincts.filter((p) => sel.selected.has(p));
      if (picked.length > 0) {
        out.push({ kind: 'subregion', ld: sel.ld, precincts: picked });
      }
    } else if (sel.on) {
      out.push({ kind: 'ld', ld: sel.ld });
    }
  }
  return out;
}

/** True when at least one scope/precinct is selected. */
function hasAnySelection(selections: ScopeSelection[]): boolean {
  return selections.some((s) =>
    s.kind === 'subregion' ? s.selected.size > 0 : s.on,
  );
}

/** Human summary of a scope grant for the success banner. */
function summarizeScopes(scopes: DelegateScopeInput[]): string {
  if (scopes.length === 0) return 'no turf';
  return scopes
    .map((s) =>
      s.kind === 'ld'
        ? `LD ${s.ld}`
        : `LD ${s.ld} — precincts ${(s.precincts ?? []).join(', ')}`,
    )
    .join('; ');
}

function errorDetail(err: unknown): string {
  const maybe = err as { response?: { data?: { detail?: unknown }; status?: number } };
  const detail = maybe?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  const status = maybe?.response?.status;
  if (status) return `Request failed (${status}). Please try again.`;
  return 'Could not reach the backend. Please try again.';
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

export interface MyVolunteersViewProps {
  userRole?: string;
}

export function MyVolunteersView({ userRole }: MyVolunteersViewProps) {
  const roleNorm = typeof userRole === 'string' ? userRole.toUpperCase() : '';
  if (roleNorm !== 'CANDIDATE') {
    return (
      <div className="space-y-6">
        <ViewHeader
          title={
            <span className="flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-accent" />
              My Volunteers
            </span>
          }
          tagline="Add field help scoped to your turf"
        />
        <div className="max-w-2xl rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="text-lg font-semibold text-status-danger mb-2 flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" aria-hidden="true" />
            Access restricted
          </h2>
          <p className="text-fg-secondary">
            My Volunteers is available to candidate accounts only. If you manage
            candidates for your committee, use the Candidates and Users admin
            views instead.
          </p>
        </div>
      </div>
    );
  }

  return <MyVolunteersInner />;
}

function MyVolunteersInner() {
  const isMobile = useIsMobile();

  const [items, setItems] = useState<MyCandidate[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  // Bumped after a successful delegation so the roster below the form picks
  // up the new volunteer without a page reload — the whole point of adding a
  // read to what used to be a write-only screen.
  const [rosterReload, setRosterReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    listMyCandidates()
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(errorDetail(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload]);

  const delegable = useMemo(
    () => (items ?? []).filter((c) => c.relation === 'candidate' && c.can_delegate),
    [items],
  );
  const candidate = delegable[0] ?? null;

  // The roster shows for ANY own-candidacy link, delegable or not. A candidate
  // whose can_delegate is off can't add volunteers, but their committee may
  // well have staffed their race — hiding the team because the ADD half is
  // locked would repeat the write-only-screen mistake this section fixes.
  const ownCandidate = useMemo(
    () => (items ?? []).find((c) => c.relation === 'candidate') ?? null,
    [items],
  );

  // A linked candidate name for context in the empty state, even when not delegable.
  const linkedName = useMemo(() => {
    if (!items || items.length === 0) return null;
    const own = items.find((c) => c.relation === 'candidate');
    return (own ?? items[0]).full_name;
  }, [items]);

  return (
    <div className="space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-accent" />
            My Volunteers
          </span>
        }
        tagline="Add field volunteers scoped to a subset of your own turf"
      />

      {loadError && (
        <div className="bg-status-danger-bg/30 rounded-xl p-4 border border-status-danger-bg text-status-danger text-sm flex items-center justify-between gap-3">
          <span>{loadError}</span>
          <button
            type="button"
            onClick={() => setReload((n) => n + 1)}
            className="bg-accent hover:bg-accent-hover text-white px-3 py-1.5 rounded text-xs font-bold"
          >
            Retry
          </button>
        </div>
      )}

      {loading && !items && (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-64" />
        </div>
      )}

      {!loading && !loadError && !candidate && (
        <div className="max-w-2xl rounded-xl border border-border-base bg-bg-raised p-6">
          <h2 className="text-lg font-semibold text-fg-primary mb-2">
            No volunteers to add
          </h2>
          <p className="text-fg-secondary">
            You don&apos;t have permission to add volunteers — ask your committee
            admin.
          </p>
          {linkedName && (
            <p className="text-fg-muted text-sm mt-3">
              You&apos;re linked to <span className="text-fg-secondary font-medium">{linkedName}</span>.
            </p>
          )}
        </div>
      )}

      {/* The roster leads the page: "who is on my race" is the question a
          candidate arrives with; adding someone is the follow-up action. It
          starts collapsed to a one-line summary so the add form stays above
          the fold — the header carries the counts either way. */}
      {!loading && !loadError && ownCandidate && (
        <TeamRoster
          candidateId={ownCandidate.candidate_id}
          refreshKey={rosterReload}
        />
      )}

      {!loading && !loadError && candidate && (
        <DelegateForm
          candidate={candidate}
          isMobile={isMobile}
          onCreated={() => setRosterReload((n) => n + 1)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The roster — who is already on this race
// ---------------------------------------------------------------------------

/**
 * Field volunteers vs committee staff is derived from role tier, because
 * that's the only claim the data can back: delegation mints only
 * canvasser/viewer, and nothing records who created any given link. The
 * committee group is read-only ON PURPOSE — a candidate has no authority to
 * edit or remove an ORG_MANAGER, so offering a button that 403s (or quietly
 * hiding those people, leaving the candidate to think they have no help)
 * would both be worse than a labelled, unactionable list.
 */
const FIELD_ROLES = new Set(['canvasser', 'viewer']);

/**
 * The collapsed header's one-line count summary — "2 org managers · 1
 * analyst · 1 canvasser". Grouped by role because that is the grouping the
 * expanded view uses; largest group first so the line front-loads the
 * headline. Exported for the tests.
 */
export function summarizeTeam(members: CandidateStaffMember[]): string {
  if (members.length === 0) return 'No one else on your race yet';
  const counts = new Map<string, number>();
  for (const m of members) {
    const label = m.role.toLowerCase().replace(/_/g, ' ');
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([label, n]) => `${n} ${label}${n === 1 ? '' : 's'}`)
    .join(' · ');
}

function TeamRoster({
  candidateId,
  refreshKey,
}: {
  candidateId: number;
  refreshKey: number;
}) {
  const [members, setMembers] = useState<CandidateStaffMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Collapsed by default: the header line already answers "who's on my
  // race" in counts, and the add form below stays above the fold.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    listCandidateStaff(candidateId)
      .then((res) => {
        if (cancelled) return;
        // The caller's own candidate-relation row is themselves, not team.
        setMembers(res.members.filter((m) => m.relation !== 'candidate'));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(errorDetail(err));
      });
    return () => {
      cancelled = true;
    };
  }, [candidateId, refreshKey]);

  // A successful add bumps refreshKey — pop the roster open so the new
  // volunteer is SEEN to appear, not just silently refetched behind a
  // collapsed header.
  useEffect(() => {
    if (refreshKey > 0) setOpen(true);
  }, [refreshKey]);

  const pendingInvites =
    members?.filter((m) => m.invite_pending).length ?? 0;

  return (
    <div className="rounded-xl border border-border-base bg-bg-raised">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="team-roster-body"
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-bg-base/40 rounded-xl transition"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-fg-muted shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="w-4 h-4 text-fg-muted shrink-0" aria-hidden="true" />
        )}
        <Users className="w-5 h-5 text-accent shrink-0" aria-hidden="true" />
        <span className="text-base font-semibold text-fg-primary shrink-0">
          Your team
        </span>
        <span className="text-sm text-fg-secondary truncate">
          {error
            ? "couldn't load — expand for details"
            : members === null
              ? 'Loading…'
              : summarizeTeam(members)}
        </span>
        {pendingInvites > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-md border border-status-warning/50 bg-status-warning-bg/30 px-2 py-0.5 text-[11px] text-status-warning shrink-0">
            <MailWarning className="w-3.5 h-3.5" aria-hidden="true" />
            {pendingInvites} invite{pendingInvites === 1 ? '' : 's'} pending
          </span>
        )}
      </button>

      {open && (
        <div id="team-roster-body" className="px-5 pb-5 pt-1 space-y-5">
          {error && (
            <div className="bg-status-danger-bg/30 rounded-lg p-3 border border-status-danger-bg text-status-danger text-sm">
              Couldn&apos;t load your team: {error}
            </div>
          )}
          {!error && members === null && <Skeleton className="h-40" />}
          {!error && members !== null && (
            <TeamRosterBody members={members} />
          )}
        </div>
      )}
    </div>
  );
}

function TeamRosterBody({ members }: { members: CandidateStaffMember[] }) {
  const field = members.filter((m) => FIELD_ROLES.has(m.role.toLowerCase()));
  const committee = members.filter(
    (m) => !FIELD_ROLES.has(m.role.toLowerCase()),
  );

  return (
    <div className="space-y-5">
      <section>
        <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-2">
          Your volunteers
        </div>
        {field.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            No field volunteers yet — add one above and they&apos;ll appear
            here.
          </p>
        ) : (
          <ul className="divide-y divide-border-base/60">
            {field.map((m) => (
              <RosterRow key={m.user_id} member={m} />
            ))}
          </ul>
        )}
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-2">
          Also working your race
        </div>
        {committee.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            No committee staff assigned.
          </p>
        ) : (
          <>
            <ul className="divide-y divide-border-base/60">
              {committee.map((m) => (
                <RosterRow key={m.user_id} member={m} />
              ))}
            </ul>
            <p className="text-xs text-fg-muted mt-2">
              Assigned by your committee — contact your committee admin to
              change these.
            </p>
          </>
        )}
      </section>
    </div>
  );
}

function RosterRow({ member }: { member: CandidateStaffMember }) {
  const roleLabel = member.role.toLowerCase().replace(/_/g, ' ');
  return (
    <li className="py-2.5 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm text-fg-primary font-medium truncate">
          {member.full_name || member.email}
        </div>
        {/* Only when it adds information — a nameless member already shows
            their email on the line above, and showing it twice reads as a
            rendering bug. */}
        {member.full_name && (
          <div className="text-xs text-fg-muted truncate">{member.email}</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {member.invite_pending && (
          <span className="inline-flex items-center gap-1 rounded-md border border-status-warning/50 bg-status-warning-bg/30 px-2 py-0.5 text-[11px] text-status-warning">
            <MailWarning className="w-3.5 h-3.5" aria-hidden="true" />
            Invite pending
          </span>
        )}
        {!member.is_active && (
          <span className="rounded-md border border-border-base bg-bg-base px-2 py-0.5 text-[11px] text-fg-muted">
            Deactivated
          </span>
        )}
        <span className="rounded-md border border-border-base bg-bg-base px-2 py-0.5 text-[11px] text-fg-secondary capitalize">
          {roleLabel}
        </span>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Candidate context card
// ---------------------------------------------------------------------------

function CandidateCard({ candidate }: { candidate: MyCandidate }) {
  return (
    <div className="rounded-xl border border-border-base bg-bg-raised p-5">
      <div className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-1">
        Your candidacy
      </div>
      <h2 className="text-xl font-bold text-fg-primary leading-tight">
        {candidate.full_name}
      </h2>
      <div className="text-fg-secondary text-sm mt-0.5">{candidate.office}</div>
      {candidate.district_label && (
        <div className="text-fg-muted text-sm">{candidate.district_label}</div>
      )}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1.5">
          Your turf
        </div>
        {candidate.scopes.length === 0 ? (
          <div className="text-sm text-fg-secondary">
            County-wide — you may scope volunteers to any of your turf.
          </div>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {candidate.scopes.map((s, i) => (
              <li
                key={`${s.kind}-${s.ld}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-border-base bg-bg-base px-2.5 py-1 text-xs text-fg-secondary"
              >
                <MapPin className="w-3.5 h-3.5 text-fg-muted" aria-hidden="true" />
                {s.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The delegation form
// ---------------------------------------------------------------------------

const ROLE_HELP: Record<DelegatedRole, string> = {
  canvasser: 'Knocks doors and logs responses in your turf.',
  viewer: 'Read-only turf analytics — no door knocking.',
};

function DelegateForm({
  candidate,
  isMobile,
  onCreated,
}: {
  candidate: MyCandidate;
  isMobile: boolean;
  onCreated?: () => void;
}) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<DelegatedRole>('canvasser');
  const [selections, setSelections] = useState<ScopeSelection[]>(() =>
    initSelections(candidate.scopes),
  );

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the scope picker if the candidate changes underneath us.
  useEffect(() => {
    setSelections(initSelections(candidate.scopes));
  }, [candidate.candidate_id, candidate.scopes]);

  const hasScopeGroups = candidate.scopes.length > 0;
  const anySelected = hasScopeGroups ? hasAnySelection(selections) : true;
  const passwordOk = password.length >= 8;
  const emailOk = /.+@.+\..+/.test(email);
  const canSubmit = emailOk && passwordOk && anySelected && !submitting;

  const togglePrecinct = useCallback((groupIdx: number, precinct: string) => {
    setSelections((prev) =>
      prev.map((sel, i) => {
        if (i !== groupIdx || sel.kind !== 'subregion') return sel;
        const next = new Set(sel.selected);
        if (next.has(precinct)) next.delete(precinct);
        else next.add(precinct);
        return { ...sel, selected: next };
      }),
    );
  }, []);

  const toggleLd = useCallback((groupIdx: number) => {
    setSelections((prev) =>
      prev.map((sel, i) =>
        i === groupIdx && sel.kind === 'ld' ? { ...sel, on: !sel.on } : sel,
      ),
    );
  }, []);

  const resetForm = useCallback(() => {
    setFullName('');
    setEmail('');
    setPassword('');
    setRole('canvasser');
    setSelections(initSelections(candidate.scopes));
  }, [candidate.scopes]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit) return;
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      // Zero scopes on the candidate = county-wide; send an empty list and let
      // the server bound it to the candidate's own reach.
      const scopes = hasScopeGroups ? buildScopes(selections) : [];
      const trimmedName = fullName.trim();
      try {
        const created = await delegateStaff(candidate.candidate_id, {
          email: email.trim(),
          password,
          full_name: trimmedName || undefined,
          role,
          scopes,
        });
        setSuccess(
          created.attached_existing
            ? // They already had an account — another campaign's volunteer, or
              // one an admin set up. Telling them to "share their temporary
              // password" here would be wrong twice over: no password was set,
              // and it implies a new account that does not exist.
              `${created.email} already had an account and is now on your team too, with your turf (${summarizeScopes(scopes)}) added to what they already cover. They sign in the way they always have.`
            : `Added ${created.email} as ${created.role}, scoped to ${summarizeScopes(scopes)}. Share their temporary password so they can sign in and change it.`,
        );
        resetForm();
        onCreated?.();
      } catch (err) {
        setError(errorDetail(err));
      } finally {
        setSubmitting(false);
      }
    },
    [
      canSubmit,
      hasScopeGroups,
      selections,
      fullName,
      email,
      password,
      role,
      candidate.candidate_id,
      resetForm,
      onCreated,
    ],
  );

  const fieldGrid = isMobile
    ? 'grid grid-cols-1 gap-4'
    : 'grid grid-cols-1 sm:grid-cols-2 gap-4';

  return (
    <div className="space-y-6">
      <CandidateCard candidate={candidate} />

      {success && (
        <div className="rounded-xl border border-status-success-bg bg-status-success-bg/30 p-4 text-sm text-status-success">
          {success}
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-status-danger-bg bg-status-danger-bg/30 p-4 text-sm text-status-danger">
          {error}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="rounded-xl border border-border-base bg-bg-raised p-5 space-y-5"
      >
        <div>
          <h3 className="text-base font-semibold text-fg-primary">Add a volunteer</h3>
          <p className="text-sm text-fg-secondary mt-0.5">
            They&apos;ll be able to work only the turf you grant below.
          </p>
        </div>

        <div className={fieldGrid}>
          <Field label="Full name" hint="Optional — how they appear to your team.">
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Volunteer"
              autoComplete="off"
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
            />
          </Field>

          <Field label="Email" required>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              autoComplete="off"
              required
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm"
            />
            {email.length > 0 && !emailOk && (
              <span className="text-[11px] text-status-danger">Enter a valid email address.</span>
            )}
          </Field>

          <Field
            label="Temporary password"
            required
            hint="Min 8 characters. Share it with the volunteer — they can change it after signing in."
          >
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="off"
              required
              minLength={8}
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary placeholder-fg-muted text-sm font-mono"
            />
            {password.length > 0 && !passwordOk && (
              <span className="text-[11px] text-status-danger">
                Must be at least 8 characters.
              </span>
            )}
          </Field>

          <Field label="Role" required hint={ROLE_HELP[role]}>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as DelegatedRole)}
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
            >
              <option value="canvasser">Canvasser — knocks doors</option>
              <option value="viewer">Viewer — read-only turf analytics</option>
            </select>
          </Field>
        </div>

        {/* Scope picker */}
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-fg-primary">Turf access</div>
            <p className="text-xs text-fg-muted mt-0.5">
              Grant a subset of your own turf. You can&apos;t grant beyond your reach.
            </p>
          </div>

          {!hasScopeGroups ? (
            <div className="rounded-lg border border-border-base bg-bg-base p-3 text-sm text-fg-secondary">
              Your candidacy is county-wide, so this volunteer inherits your full
              reach automatically.
            </div>
          ) : (
            <div className="space-y-3">
              {selections.map((sel, i) => (
                <ScopeGroup
                  key={`${sel.kind}-${sel.ld}-${i}`}
                  selection={sel}
                  isMobile={isMobile}
                  onTogglePrecinct={(p) => togglePrecinct(i, p)}
                  onToggleLd={() => toggleLd(i)}
                />
              ))}
            </div>
          )}

          {hasScopeGroups && !anySelected && (
            <p className="text-[11px] text-status-warning">
              Select at least one precinct or district to add this volunteer.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 rounded bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2"
          >
            <UserPlus className="w-4 h-4" aria-hidden="true" />
            {submitting ? 'Adding…' : 'Add volunteer'}
          </button>
          <span className="text-[11px] text-fg-muted">
            Volunteers you add appear in the admin user list.
          </span>
        </div>
      </form>
    </div>
  );
}

function ScopeGroup({
  selection,
  isMobile,
  onTogglePrecinct,
  onToggleLd,
}: {
  selection: ScopeSelection;
  isMobile: boolean;
  onTogglePrecinct: (precinct: string) => void;
  onToggleLd: () => void;
}) {
  if (selection.kind === 'ld') {
    return (
      <div className="rounded-lg border border-border-base bg-bg-base p-3">
        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={selection.on}
            onChange={onToggleLd}
            className="h-4 w-4 accent-accent"
          />
          <span className="text-sm text-fg-primary font-medium">
            Entire LD {selection.ld}
          </span>
        </label>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border-base bg-bg-base p-3">
      <div className="text-xs uppercase tracking-wide text-fg-muted font-semibold mb-2">
        LD {selection.ld} precincts
      </div>
      {selection.precincts.length === 0 ? (
        <div className="text-sm text-fg-secondary">No precincts in this group.</div>
      ) : (
        <div className={isMobile ? 'flex flex-wrap gap-2' : 'flex flex-wrap gap-2'}>
          {selection.precincts.map((p) => {
            const on = selection.selected.has(p);
            return (
              <button
                key={p}
                type="button"
                onClick={() => onTogglePrecinct(p)}
                aria-pressed={on}
                className={
                  on
                    ? 'rounded-full border border-accent bg-accent/15 text-accent text-xs font-semibold px-3 py-1'
                    : 'rounded-full border border-border-base bg-bg-raised text-fg-muted text-xs font-semibold px-3 py-1 hover:text-fg-secondary'
                }
              >
                Precinct {p}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
        {label}
        {required && <span className="text-status-danger ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="text-[11px] text-fg-muted">{hint}</span>}
    </label>
  );
}

export default MyVolunteersView;
